import type { UIMessage } from "@ai-sdk/react";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { memo } from "react";
import { cn } from "src/common/utils";

import { Avatar, AvatarFallback, AvatarImage } from "../avatar";
import { Button, ButtonProps } from "../button";
import { Tooltip } from "../tooltip";
import { formatChatTimestamp } from "./utils";

type AnyUIMessage = UIMessage<Record<string, unknown>>;

export interface ChatBubbleProps {
  uiMessage?: AnyUIMessage;
  role?: "human" | "agent";
  timestamp?: string;
  startedAt?: string;
  completedAt?: string;
  className?: string;
  avatarSrc?: string;
  avatarFallback?: string;
  avatarContent?: React.ReactNode;
  hideAvatar?: boolean;
  children: React.ReactNode;
  /** Optional content rendered between the message body and the timestamp. */
  belowMessage?: React.ReactNode;
}

/**
 * Pure ChatBubble component that handles layout and styling
 */
export const ChatBubble = memo<ChatBubbleProps>(function ChatBubble({
  uiMessage,
  role,
  timestamp,
  startedAt,
  completedAt,
  className,
  avatarSrc,
  avatarFallback,
  avatarContent,
  hideAvatar = false,
  children,
  belowMessage,
}) {
  const metadata = (uiMessage?.metadata || {}) as Record<string, unknown>;
  const metadataRole = typeof metadata["messageRole"] === "string" ? (metadata["messageRole"] as string) : undefined;

  const roleFromMessage = React.useMemo(() => {
    if (!uiMessage) {
      return undefined;
    }

    if (uiMessage.role === "assistant") {
      return "agent" as const;
    }
    if (uiMessage.role === "user") {
      return "human" as const;
    }

    if (metadataRole === "AGENT") {
      return "agent" as const;
    }
    if (metadataRole === "HUMAN") {
      return "human" as const;
    }

    return undefined;
  }, [uiMessage, metadataRole]);

  const resolvedRole: "human" | "agent" = role ?? roleFromMessage ?? "agent";

  const metadataTimestamp = typeof metadata["formattedTimestamp"] === "string"
    ? (metadata["formattedTimestamp"] as string)
    : undefined;
  const metadataCreatedAt = typeof metadata["createdAt"] === "string"
    ? (metadata["createdAt"] as string)
    : undefined;
  const metadataUpdatedAt = typeof metadata["updatedAt"] === "string"
    ? (metadata["updatedAt"] as string)
    : undefined;

  const resolvedTimestamp =
    timestamp ||
    metadataTimestamp ||
    (metadataCreatedAt ? formatChatTimestamp(metadataCreatedAt) : undefined);

  const resolvedStartedAt = startedAt || (metadataCreatedAt ? formatChatTimestamp(metadataCreatedAt) : undefined);
  const resolvedCompletedAt = completedAt || (metadataUpdatedAt ? formatChatTimestamp(metadataUpdatedAt) : undefined);

  const variant = resolvedRole === "human" ? "sent" : "received";
  const defaultFallback = resolvedRole === "human" ? "U" : "T0";
  // Use avatarFallback if it's defined (even if empty string), otherwise use defaultFallback
  const finalFallback = avatarFallback !== undefined ? avatarFallback : defaultFallback;

  return (
    <BaseChatBubble variant={variant} className={className}>
      {!hideAvatar && (
        <>
          {avatarContent ? (
            <div className="hidden md:flex flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden">
              {avatarContent}
            </div>
          ) : (
            <ChatBubbleAvatar
              src={avatarSrc}
              fallback={finalFallback}
              role={resolvedRole}
              className="hidden md:flex w-8 h-8"
            />
          )}
        </>
      )}
      <div className={cn("flex flex-col min-w-0", variant === "received" ? "w-full" : "items-end")}>
        <ChatBubbleMessage variant={variant}>
          {children}
        </ChatBubbleMessage>
        {belowMessage && <div className="mt-2">{belowMessage}</div>}
        {resolvedTimestamp && (
          <ChatBubbleTimestamp
            timestamp={resolvedTimestamp}
            startedAt={resolvedStartedAt}
            completedAt={resolvedCompletedAt}
            className={variant === "sent" ? "text-right" : "text-left"}
          />
        )}
      </div>
    </BaseChatBubble>
  );
});

// Base Components
const chatBubbleVariant = cva(
  "flex gap-2 items-end relative group animate-message-in",
  {
    variants: {
      variant: {
        received: "self-start w-full max-w-7xl",
        sent: "self-end flex-row-reverse max-w-full",
      },
      layout: {
        default: "",
        ai: "max-w-full w-full items-center",
      },
    },
    defaultVariants: {
      variant: "received",
      layout: "default",
    },
  },
);

interface BaseChatBubbleProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof chatBubbleVariant> {}

const BaseChatBubble = React.forwardRef<HTMLDivElement, BaseChatBubbleProps>(
  ({ className, variant, layout, children, ...props }, ref) => (
    <div
      className={cn(
        chatBubbleVariant({ variant, layout, className }),
        "relative group",
      )}
      ref={ref}
      {...props}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child) && typeof child.type !== "string"
          ? React.cloneElement(child, {
              variant,
              layout,
            } as React.ComponentProps<typeof child.type>)
          : child,
      )}
    </div>
  ),
);
BaseChatBubble.displayName = "BaseChatBubble";

// ChatBubbleAvatar
interface ChatBubbleAvatarProps {
  src?: string;
  fallback?: string;
  className?: string;
  role?: "human" | "agent";
}

const ChatBubbleAvatar: React.FC<ChatBubbleAvatarProps> = ({
  src,
  fallback,
  className,
  role,
}) => {
  // Different styling for agent vs user avatars
  const isAgent = role ? role === "agent" : fallback === "T0";

  return (
    <Avatar className={cn(
      isAgent ? "text-white bg-blue-600 shadow-lg shadow-blue-500/20" : "text-foreground",
      className,
      "transition-all duration-200"
    )}>
      <AvatarImage src={src} alt="Avatar" />
      <AvatarFallback>{fallback}</AvatarFallback>
    </Avatar>
  );
};

// ChatBubbleMessage
const chatBubbleMessageVariants = cva("px-5 py-3 transition-all duration-200", {
  variants: {
    variant: {
      received: "bg-popover border border-accent text-foreground rounded-2xl hover:bg-secondary",
      sent: "bg-gradient-to-br from-gray-800 to-black dark:from-blue-600 dark:to-blue-500 text-white [&_*]:text-white rounded-2xl",
    },
    layout: {
      default: "",
      ai: "border-t w-full rounded-none bg-transparent",
    },
  },
  defaultVariants: {
    variant: "received",
    layout: "default",
  },
});

interface ChatBubbleMessageProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof chatBubbleMessageVariants> {}

const ChatBubbleMessage = React.forwardRef<
  HTMLDivElement,
  ChatBubbleMessageProps
>(
  (
    {
      className,
      variant,
      layout,
      children,
      ...props
    },
    ref,
  ) => (
    <div
      className={cn(
        chatBubbleMessageVariants({ variant, layout, className }),
        "break-words overflow-hidden min-w-0",
        // w-[1px] min-w-full: force a concrete width so overflow-hidden clips wide
        // content (tables, code) instead of expanding the flex parent.
        variant === "received" ? "w-[1px] min-w-full" : "",
      )}
      ref={ref}
      {...props}
    >
      {children}
    </div>
  ),
);
ChatBubbleMessage.displayName = "ChatBubbleMessage";

// ChatBubbleTimestamp
interface ChatBubbleTimestampProps
  extends React.HTMLAttributes<HTMLDivElement> {
  timestamp: string;
  startedAt?: string;
  completedAt?: string;
}

const ChatBubbleTimestamp = memo<ChatBubbleTimestampProps>(({
  timestamp,
  startedAt,
  completedAt,
  className,
  ...props
}) => {
  const showTooltip = startedAt && completedAt && startedAt !== completedAt;
  const tooltipContent = showTooltip ? `Started: ${startedAt}\nCompleted: ${completedAt}` : "";

  const timestampElement = (
    <div
      className={cn("text-xs mt-1 text-right text-muted-foreground transition-opacity whitespace-nowrap", className)}
      {...props}
    >
      {timestamp}
    </div>
  );

  if (showTooltip) {
    return (
      <Tooltip content={tooltipContent} position="top" delay={200} multiLine>
        {timestampElement}
      </Tooltip>
    );
  }

  return timestampElement;
});
ChatBubbleTimestamp.displayName = "ChatBubbleTimestamp";

// ChatBubbleAction
type ChatBubbleActionProps = ButtonProps & {
  icon: React.ReactNode;
};

const ChatBubbleAction: React.FC<ChatBubbleActionProps> = ({
  icon,
  onClick,
  className,
  variant = "ghost",
  size = "icon",
  ...props
}) => (
  <Button
    variant={variant}
    size={size}
    className={className}
    onClick={onClick}
    {...props}
  >
    {icon}
  </Button>
);

interface ChatBubbleActionWrapperProps
  extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "sent" | "received";
  className?: string;
}

const ChatBubbleActionWrapper = React.forwardRef<
  HTMLDivElement,
  ChatBubbleActionWrapperProps
>(({ variant, className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "absolute top-1/2 -translate-y-1/2 flex opacity-0 group-hover:opacity-100 transition-opacity duration-200",
      variant === "sent"
        ? "-left-1 -translate-x-full flex-row-reverse"
        : "-right-1 translate-x-full",
      className,
    )}
    {...props}
  >
    {children}
  </div>
));
ChatBubbleActionWrapper.displayName = "ChatBubbleActionWrapper";

export {
  BaseChatBubble,
  ChatBubbleAction,
  ChatBubbleActionWrapper,
  ChatBubbleAvatar,
  ChatBubbleMessage,
  chatBubbleMessageVariants,
  ChatBubbleTimestamp,
  chatBubbleVariant,
};
